import { jsx as _jsx } from "react/jsx-runtime";
import getItemUrl from "../getItemUrl";
import Actor from "./Actor";
import To from "./To";
/**
 * CONVENTIONS:
 * Always use `oldThing` unless there's both an `oldThing` and `newThing` - this
 * means that we can use `a thing` (for oldThing) and `another thing` (for
 * newThing) and have it make sense.
 *
 * Always ensure you handle the object not existing - it may have been deleted
 * since the activity was created.
 */ function makeItemAction(action, currentUser) {
    return function itemAction(activity, context = {}) {
        var ref;
        const { actor , item  } = activity;
        if (!item) throw new Error("Insufficient data");
        const withItem = item.title || item.description;
        const url = getItemUrl(item, (ref = item.rootTopicOrSelf) === null || ref === void 0 ? void 0 : ref.owner, true);
        const thisThing = context.subject && context.subject.id === item.id ? `this ${item.type.toLowerCase()}` : null;
        return {
            url,
            template: withItem ? `{actor} ${action} {item}` : `{actor} ${action}`,
            variables: {
                actor: /*#__PURE__*/ _jsx(Actor, {
                    actor: actor,
                    currentUser: currentUser
                }),
                item: thisThing || (withItem && url ? /*#__PURE__*/ _jsx(To, {
                    label: withItem,
                    url: url
                }) : undefined)
            }
        };
    };
}
const makeLinkChangeAction = (action, currentUser)=>(activity)=>{
        const { actor , item , rootPost , data  } = activity;
        if (!item || !rootPost || !item.parentTopic) throw new Error("Missing item data");
        const { parentTopic  } = item;
        const url = getItemUrl(rootPost);
        const { oldUrl , newUrl  } = data || {};
        // This is a little verbose because we're not allowed to nest ternaries
        const template = (()=>{
            switch(action){
                case "added":
                    return "On {item}, {actor} added link to {newLink}";
                case "removed":
                    return "On {item}, {actor} removed link {oldLink}";
                case "updated":
                    return "On {item}, {actor} updated link from {oldLink} to {newLink}";
                default:
                    throw new Error("Impossible!");
            }
        })();
        return {
            // TODO: permalink to item
            url,
            template,
            variables: {
                actor: /*#__PURE__*/ _jsx(Actor, {
                    actor: actor,
                    currentUser: currentUser,
                    lowerCase: true
                }),
                oldLink: oldUrl ? /*#__PURE__*/ _jsx("a", {
                    href: String(oldUrl),
                    children: String(oldUrl)
                }) : "(unknown)",
                newLink: newUrl ? /*#__PURE__*/ _jsx("a", {
                    href: String(newUrl),
                    children: String(newUrl)
                }) : "(unknown)",
                item: url ? /*#__PURE__*/ _jsx(To, {
                    label: rootPost.description || rootPost.linkTitle || parentTopic.description || url,
                    url: url
                }) : rootPost.description || rootPost.linkTitle || parentTopic.description || url
            }
        };
    };
function makeTopicAction(action, currentUser) {
    return function generatedTopicActionFunction(activity) {
        var ref;
        const { actor , rootTopic  } = activity;
        if (!rootTopic) throw new Error("Missing data");
        return {
            url: getItemUrl(rootTopic, (ref = rootTopic.rootTopicOrSelf) === null || ref === void 0 ? void 0 : ref.owner),
            template: `{actor} ${action} this tree`,
            variables: {
                actor: /*#__PURE__*/ _jsx(Actor, {
                    actor: actor,
                    currentUser: currentUser
                })
            }
        };
    };
}
export function getTemplates(currentUser) {
    const TEMPLATES = {
        CREATED_TOPIC: makeTopicAction("created", currentUser),
        DELETED_TOPIC: makeTopicAction("deleted", currentUser),
        UNDELETED_TOPIC: makeTopicAction("undeleted", currentUser),
        ARCHIVED_TOPIC: makeTopicAction("archived", currentUser),
        UNARCHIVED_TOPIC: makeTopicAction("unarchived", currentUser),
        REQUESTED_INVITATION: makeTopicAction("asked to join", currentUser),
        JOINED_TOPIC: makeTopicAction("joined", currentUser),
        PARTED_TOPIC: makeTopicAction("left", currentUser),
        TRANSFERRED_TOPIC (activity) {
            var ref;
            const { actor , newUser , rootTopic  } = activity;
            if (!rootTopic) throw new Error("Missing data");
            return {
                url: getItemUrl(rootTopic, rootTopic === null || rootTopic === void 0 ? void 0 : (ref = rootTopic.rootTopicOrSelf) === null || ref === void 0 ? void 0 : ref.owner),
                template: "{actor} added this tree to {user}",
                variables: {
                    actor: /*#__PURE__*/ _jsx(Actor, {
                        actor: actor,
                        currentUser: currentUser
                    }),
                    user: newUser ? /*#__PURE__*/ _jsx(To, {
                        url: `/${newUser.username}`,
                        label: newUser.username
                    }) : "a deleted user"
                }
            };
        },
        ADDED_ITEM_TO_TOPIC: makeItemAction("added", currentUser),
        INVITED (activity) {
            var ref;
            const { actor , oldUser , rootTopic  } = activity;
            if (!rootTopic) throw new Error("Missing data");
            return {
                url: getItemUrl(rootTopic, rootTopic === null || rootTopic === void 0 ? void 0 : (ref = rootTopic.rootTopicOrSelf) === null || ref === void 0 ? void 0 : ref.owner),
                template: oldUser ? "{actor} invited {user} to join this tree" : "{actor} invited a deleted user to join this tree",
                variables: {
                    actor: /*#__PURE__*/ _jsx(Actor, {
                        actor: actor,
                        currentUser: currentUser
                    }),
                    user: oldUser ? /*#__PURE__*/ _jsx(To, {
                        url: `/${oldUser.username}`,
                        label: oldUser.username
                    }) : undefined
                }
            };
        },
        COMMENTED (activity, context = {}) {
            const { rootPost , actor , comment  } = activity;
            let url;
            if (comment && rootPost) url = `${getItemUrl(rootPost)}#comment-${comment.id}${comment.parentCommentId ? `-${comment.parentCommentId}` : ""}`;
            else url = rootPost ? getItemUrl(rootPost) : null;
            const thisThing = context.subject && rootPost && context.subject.id === rootPost.id ? `this ${rootPost.type.toLowerCase()}` : null;
            return {
                url,
                template: rootPost && rootPost.description ? "{actor} commented on {item}" : "{actor} commented on",
                variables: {
                    actor: /*#__PURE__*/ _jsx(Actor, {
                        actor: actor,
                        currentUser: currentUser
                    }),
                    item: thisThing || (rootPost && rootPost.description && url ? /*#__PURE__*/ _jsx(To, {
                        label: rootPost.description,
                        url: url
                    }) : undefined)
                }
            };
        },
        // the only `itemType` supported here is `Checklist_Item`
        ADDED_ITEM_TO_POST (activity, context = {}) {
            const { item , actor , rootPost  } = activity;
            if (!item || !rootPost || !item.parentTopic) throw new Error("Insufficient data");
            // parentTopic here is the `Checklist`
            const { parentTopic  } = item;
            const url = getItemUrl(rootPost);
            const thisThing = context.subject && rootPost && context.subject.id === rootPost.id ? `this ${rootPost.type.toLowerCase()}` : null;
            return {
                // TODO: permalink to item
                url,
                template: "On {item}, {actor} added",
                variables: {
                    actor: /*#__PURE__*/ _jsx(Actor, {
                        actor: actor,
                        currentUser: currentUser,
                        lowerCase: true
                    }),
                    item: thisThing || url && /*#__PURE__*/ _jsx(To, {
                        label: rootPost.description || rootPost.linkTitle || parentTopic.description || url,
                        url: url
                    })
                }
            };
        },
        COMPLETED_CHECKLIST_ITEM (activity) {
            const { actor , item , rootPost  } = activity;
            if (!item || !rootPost || !item.parentTopic) throw new Error("Insufficient data");
            const { parentTopic  } = item;
            const url = getItemUrl(rootPost);
            return {
                // TODO: permalink to item
                url,
                template: "On {item}, {actor} checked off",
                variables: {
                    actor: /*#__PURE__*/ _jsx(Actor, {
                        currentUser: currentUser,
                        actor: actor,
                        lowerCase: true
                    }),
                    item: url && /*#__PURE__*/ _jsx(To, {
                        label: rootPost.description || rootPost.linkTitle || parentTopic.description || url,
                        url: url
                    })
                }
            };
        },
        UNCOMPLETED_CHECKLIST_ITEM (activity) {
            const { actor , item , rootPost  } = activity;
            if (!item || !rootPost || !item.parentTopic) throw new Error("Insufficient data");
            const { parentTopic  } = item;
            const url = getItemUrl(rootPost);
            return {
                // TODO: permalink to item
                url,
                template: "On {item}, {actor} unchecked",
                variables: {
                    actor: /*#__PURE__*/ _jsx(Actor, {
                        currentUser: currentUser,
                        actor: actor,
                        lowerCase: true
                    }),
                    item: url && /*#__PURE__*/ _jsx(To, {
                        label: rootPost.description || rootPost.linkTitle || parentTopic.description || url,
                        url: url
                    })
                }
            };
        },
        COMPLETED_ITEM (activity) {
            const { actor , item  } = activity;
            if (!item) throw new Error("Insufficient data");
            const { parentTopic  } = item;
            const url = getItemUrl(item);
            return {
                // TODO: permalink to item
                url,
                template: "{actor} checked off {item}",
                variables: {
                    actor: /*#__PURE__*/ _jsx(Actor, {
                        currentUser: currentUser,
                        actor: actor
                    }),
                    item: url && /*#__PURE__*/ _jsx(To, {
                        label: item.description || item.title || (parentTopic ? `an item in ${parentTopic.description}` : `an item`),
                        url: url
                    })
                }
            };
        },
        UNCOMPLETED_ITEM (activity) {
            const { actor , item  } = activity;
            if (!item) throw new Error("Insufficient data");
            const { parentTopic  } = item;
            const url = getItemUrl(item);
            return {
                // TODO: permalink to item
                url,
                template: "{actor} unchecked {item}",
                variables: {
                    actor: /*#__PURE__*/ _jsx(Actor, {
                        currentUser: currentUser,
                        actor: actor
                    }),
                    item: url && /*#__PURE__*/ _jsx(To, {
                        label: item.description || item.title || (parentTopic ? `an item in ${parentTopic.description}` : `an item`),
                        url: url
                    })
                }
            };
        },
        ARCHIVED_ITEM: makeItemAction("archived", currentUser),
        UNARCHIVED_ITEM: makeItemAction("unarchived", currentUser),
        DELETED_ITEM: makeItemAction("deleted", currentUser),
        UNDELETED_ITEM: makeItemAction("undeleted", currentUser),
        MOVED_ITEM: makeItemAction("moved", currentUser),
        TRANSFERRED_ITEM_IN: makeItemAction("moved", currentUser),
        TRANSFERRED_ITEM_OUT: makeItemAction("moved", currentUser),
        UPDATED_LINK_FOR_POST: makeLinkChangeAction("updated", currentUser),
        ADDED_LINK_TO_POST: makeLinkChangeAction("added", currentUser),
        REMOVED_LINK_FROM_POST: makeLinkChangeAction("removed", currentUser)
    };
    return TEMPLATES;
}
